<template>
  <div>
    <v-card class="och-testPlanCard__border--bottom elevation-0">
      <v-row row>
        <v-col sm="6">
          <v-card class="elevation-0 och-testPlanCard__border--right">
            <v-card-title class="pb-0">
              <span class="pt-2 font-weight-black primary--text subtitle-1">UAT PURPOSE</span>
            </v-card-title>
            <v-card-text class="pt-2">
              <div class="och-field--inline-notEditable px-3 pt-1">
                <embedded-html
                  class="mt-2"
                  :value="releaseNote.requiredSetup"
                  data-cy="testPlanTab-uatPurpose"
                  v-if="releaseNote.requiredSetup"
                ></embedded-html>
                <span class="font-italic" v-else>(No value)</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col sm="6">
          <v-card class="elevation-0">
            <v-card-title class="pb-0">
              <span class="pt-2 pb-1 font-weight-black primary--text subtitle-1">UAT ENVIRONMENTS</span>
              <v-spacer></v-spacer>
              <span v-if="hasReleaseNoteCreateEdit" class="pt-2 text-sm-right">
                <v-btn
                  icon
                  @click="isEnvironmentFormVisible = true"
                  class="mt-0"
                  data-cy="testPlanTab__button--editEnvironment"
                >
                  <v-icon small>far fa-edit</v-icon>
                </v-btn>
              </span>
            </v-card-title>
            <v-card-text v-if="!isEnvironmentFormVisible">
              <div
                v-if="hasReleaseNoteCreateEdit"
                class="och-field--inline-editable px-3 pt-1"
                @click="isEnvironmentFormVisible = true"
              >
                <span v-if="releaseNote.uatEnvironment">
                  <ul>
                    <li v-for="env in releaseNote.uatEnvironment" :key="env">{{ env }}</li>
                  </ul>
                </span>
                <span v-else><i>(No value)</i></span>
              </div>
              <div v-else class="och-field--inline-notEditable px-3 pt-1">
                <span v-if="releaseNote.uatEnvironment">
                  <ul>
                    <li v-for="env in releaseNote.uatEnvironment" :key="env">{{ env }}</li>
                  </ul>
                </span>
                <span v-else><i>(No value)</i></span>
              </div>
            </v-card-text>
            <v-card-text class="pt-2" v-if="isEnvironmentFormVisible">
              <div>
                <v-autocomplete
                  v-model="releaseNote.uatEnvironment"
                  :items="dropdowns.uatEnvironment"
                  placeholder="Choose an environment..."
                  data-cy="testPlanTab__dropdown--environment"
                  :multiple="true"
                  :clearable="true"
                ></v-autocomplete>
              </div>
              <div class="mt-2">
                <v-btn text @click="cancelEnvironment" data-cy="testPlanTab__button--cancelEnvironment">Cancel</v-btn>
                <v-btn text class="primary" @click="saveEnvironment" data-cy="testPlanTab__button--saveEnvironment"
                  >Save</v-btn
                >
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
    <individual-test-setup :testSetupItems="releaseNote.individualSetup" mode="edit"></individual-test-setup>
    <v-card>
      <v-card-title primary-title class="pb-0 mb-0">
        <span class="pt-3 font-weight-black primary--text subtitle-1">TEST STEPS ({{ testStepListLength }})</span>
        <v-spacer></v-spacer>
        <span v-if="hasReleaseNoteCreateEdit" class="text-sm-right">
          <v-btn
            @click="createTestStep"
            class="pt-2"
            data-cy="testPlanTab__button--createTestStep"
            id="e2e-createTestStepButton"
            text
          >
            <v-icon small class="mr-2">fas fa-plus</v-icon>Add Test Step
          </v-btn>
        </span>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="testStepList"
          :sort-by="['stepNumber', 'substepNumber']"
          :sort-desc="[false, false]"
          hide-default-footer
          disable-pagination
          item-key="id"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td v-if="hasReleaseNoteCreateEdit">
                <router-link :to="`/pack/${packId}/releaseNote/${releaseNoteId}/testStep/${item.id}/edit`">
                  {{ item.stepNumber }}{{ item.substepNumber ? `.${item.substepNumber}` : `` }}
                </router-link>
              </td>
              <td v-else>{{ item.stepNumber }}{{ item.substepNumber ? `.${item.substepNumber}` : `` }}</td>
              <td>
                <span v-html="item.scenario"></span>
              </td>
              <td>
                <span v-html="item.expectedOutcome"></span>
              </td>
              <td>
                <v-btn
                  icon
                  text
                  @click="showDeleteTestStepModal(item)"
                  v-if="hasReleaseNoteDelete"
                  data-cy="testPlanTab__button--deleteTestStepDialog"
                >
                  <v-icon small>far fa-trash-alt</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-dialog v-model="deleteTestStepDialog" persistent max-width="800px" v-if="hasReleaseNoteDelete">
      <v-card v-if="testStepToDelete">
        <v-card-title class="pt-3 font-weight-black primary--text subtitle-1"
          >Delete Test Step {{ testStepToDelete.stepNumber }}?</v-card-title
        >
        <v-divider></v-divider>
        <v-card-text>
          <strong>Scenario:</strong>
          <span v-html="testStepToDelete.scenario"></span>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          Do you want to delete this test step?
          <v-checkbox
            :height="10"
            :hide-details="true"
            label="Automatically shift lower test steps up?"
            v-model="shiftTestSteps"
            data-cy="testStepDialog__checkbox"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteTestStepDialog = false" data-cy="testStepDialog__button--cancel" outlined>Cancel</v-btn>
          <v-btn
            @click="deleteTestStep(testStepToDelete.id, shiftTestSteps)"
            color="red darken-2"
            dark
            data-cy="testStepDialog__button--delete"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import EmbeddedHtml from "@/components/embeddedHtml";
import IndividualTestSetup from "@/components/individualTestSetupTable";
import releaseNote from "@/api/releaseNote";

export default {
  name: "testStepTable",
  components: {
    EmbeddedHtml,
    IndividualTestSetup,
  },
  computed: {
    dropdowns() {
      return this.$store.state.dropdown.dropdowns;
    },
    releaseNote() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    testStepList() {
      return this.$store.getters["testStep/testStepList"](this.releaseNoteId);
    },
    testStepListLength() {
      // Filter out the substeps
      if (this.testStepList) {
        const filteredList = this.testStepList.filter(step => !step.substepNumber);
        return filteredList.length;
      }
    },
    ...mapGetters({
      hasReleaseNoteCreateEdit: "session/hasReleaseNoteCreateEdit",
      hasReleaseNoteDelete: "session/hasReleaseNoteDelete",
    }),
  },
  async created() {
    try {
      await this.getTestStepList(this.releaseNoteId);
    } catch (e) {
      console.error(e);
      this.$notifyError("Error fetching test step list");
    }
  },
  data() {
    return {
      deleteTestStepDialog: false,
      headers: [
        {
          text: "Step",
          align: "left",
          sortable: false,
          value: "stepNumber",
        },
        {
          text: "Scenario",
          align: "left",
          sortable: false,
          value: "scenario",
        },
        {
          text: "Expected Outcome",
          align: "left",
          sortable: false,
          value: "expectedOutcome",
        },
        {
          text: " ",
          align: "left",
          sortable: false,
          value: "",
        },
      ],
      isEnvironmentFormVisible: false,
      isRequiredSetupFormVisible: false,
      shiftTestSteps: true,
      testStepToDelete: null,
    };
  },
  methods: {
    async cancelEnvironment() {
      this.isEnvironmentFormVisible = false;
      await this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId);
    },
    async cancelRequiredSetup() {
      this.isRequiredSetupFormVisible = false;
      await this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId);
    },
    createTestStep() {
      this.$router.push({ name: "testStepCreate" });
    },
    async deleteTestStep(testStepId, shiftTestSteps) {
      try {
        await this.$store.dispatch("testStep/delete", {
          releaseNoteId: this.releaseNoteId,
          shiftTestSteps,
          testStepId,
        });
        this.deleteTestStepDialog = false;
      } catch (e) {
        console.error(e);
        this.$notifyError("Error deleting test step");
        this.deleteTestStepDialog = false;
      }
    },
    async getTestStepList(releaseNoteId, serviceArea) {
      await this.$store.dispatch("testStep/list", {
        releaseNoteId,
        serviceArea,
      });
    },
    async saveEnvironment() {
      await this.$store.dispatch("releaseNote/update");
      this.isEnvironmentFormVisible = false;
      await this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId);
    },
    showDeleteTestStepModal(testStep) {
      this.testStepToDelete = testStep;
      this.shiftTestSteps = true;
      this.deleteTestStepDialog = true;
    },
    showRequiredSetupForm() {
      if (this.hasReleaseNoteCreateEdit) {
        this.isRequiredSetupFormVisible = true;
      }
    },
  },
  props: {
    packId: {
      type: Number,
      required: true,
    },
    releaseNoteId: {
      type: Number,
      required: true,
    },
  },
  watch: {
    releaseNoteId(val) {
      this.getTestStepList(val);
    },
  },
};
</script>

<style>
.och-field--inline-editable {
  border: 1px solid transparent;
}

.och-field--inline-editable:hover {
  border: 1px solid #ccc;
  cursor: pointer;
}

.och-field--inline-notEditable {
  border: 1px solid transparent;
}
</style>
